// Shared low-poly roadwork parts — the connective-geometry kit for walls,
// fences and paving: the 8-bit neighbour-direction table in connection-mask
// order, the half-arm boundary frame (the horizontal run, height rise, pitch
// and yaw of an arm reaching the SHARED tile-boundary point — the math that
// makes two adjacent walls/fences always meet seamlessly on a slope), a
// world-Y part rotator for a gate portal, and a deterministic hash stream
// (never Math.random, so a tile always rebuilds byte-identical geometry).
// Ported from age-of-mcp's v2/roadwork-parts.ts: the pure geometry/math is kept
// verbatim, with the Part type taken from the library. The `heights` parameter
// of `armFrame` is a per-neighbour height-delta array (not a terrain grid), so
// it stays — it is what makes two arms meet flush at a shared boundary point.

import type { Part } from "../types";

/** The eight neighbour directions in connection-mask bit order: bit 0 = E,
 *  then counter-clockwise (NE, N, NW, W, SW, S, SE). Local +X is east and
 *  local +Z is north — matching how the renderer places the model and
 *  computes the mask. Even bits are orthogonal, odd bits diagonal. */
export const DIRS8: ReadonlyArray<readonly [number, number]> = [
  [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1],
];

/**
 * The frame of one half-arm reaching from this tile's centre to the shared
 * boundary point — the edge midpoint for an orthogonal neighbour, the corner
 * vertex for a diagonal one; at (dx·0.5, dz·0.5) either way. `heights[i]` is
 * (boundary height − my height), sampled by the renderer at that exact
 * terrain-mesh vertex for BOTH adjacent tiles, so arms built on this frame
 * always join flush on a slope (the classic boundary-midpoint approach).
 */
export interface ArmFrame {
  dx: number;
  dz: number;
  diagonal: boolean;
  /** Horizontal run to the boundary point (0.5 orthogonal, √½ diagonal). */
  horiz: number;
  /** Height rise from this tile's centre to the boundary point. */
  dY: number;
  /** Slope pitch. Apply as rotation [0, yaw, pitch] with euler order 'YZX'
   *  (= Ry(yaw)·Rz(pitch)): the pitch tilts the arm upward in its own local
   *  frame, then the yaw points it at the compass direction — correct for
   *  all 8 directions (matching the classic fence/wall convention). */
  pitch: number;
  yaw: number;
  /** True arm length along the slope, so the horizontal span stays exact. */
  len3D: number;
}

export function armFrame(i: number, heights: readonly number[]): ArmFrame {
  const [dx, dz] = DIRS8[i]!;
  const diagonal = (i & 1) === 1;
  const horiz = diagonal ? Math.SQRT1_2 : 0.5;
  const dY = heights[i] ?? 0;
  return {
    dx, dz, diagonal, horiz, dY,
    pitch: Math.atan2(dY, horiz),
    yaw: Math.atan2(-dz, dx),
    len3D: Math.sqrt(horiz * horiz + dY * dY),
  };
}

/** A point at parameter t (0 = tile centre, 1 = boundary point) along an
 *  arm's slope line, raised by `lift` — for studding decoration (merlons,
 *  brace shoes) along a tilted arm so it follows the terrain. */
export function armPoint(f: ArmFrame, t: number, lift: number): [number, number, number] {
  return [f.dx * 0.5 * t, f.dY * t + lift, f.dz * 0.5 * t];
}

/** Apply an additional world-Y rotation to a part list: rotates each position
 *  around the origin and adds `yaw` to the part's rotation.y. Valid when all
 *  parts have rotation.x = 0 — true for every gate-portal part. */
export function rotatePartsY(parts: Part[], yaw: number): Part[] {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  return parts.map(p => {
    const pos = p.position;
    const rot = p.rotation;
    return {
      ...p,
      position: pos
        ? [pos[0] * c - pos[2] * s, pos[1], pos[0] * s + pos[2] * c] as [number, number, number]
        : pos,
      rotation: rot
        ? [rot[0], rot[1] + yaw, rot[2]] as [number, number, number]
        : [0, yaw, 0] as [number, number, number],
    };
  });
}

/** Deterministic 0..1 hash keyed by (i, j, salt). */
export function h01(i: number, j: number, salt: number): number {
  const x = Math.sin(i * 127.1 + j * 311.7 + salt * 74.7 + 19.19) * 43758.5453;
  return x - Math.floor(x);
}
